import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock, Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";

const MAIL_TO = "info@holzbuddy.team"; // Fallback für mailto-Links

const initialForm = {
  name: '',
  email: '',
  telefon: '',
  projektart: '',
  ort: '',
  nachricht: ''
};

const projektartenDE = [
  "Aufstockung / Anbau",
  "Dachstuhl / Holztragwerk",
  "Holzhaus",
  "Hotelbau / Tourismusbau",
  "Holzfassade",
  "Sanierung / Restaurierung",
  "Hallen- / Gewerbebau",
  "Terrasse / Balkon",
  "Sonstiges"
];

const projektartenIT = [
  "Sopraelevazione / Ampliamento",
  "Capriata / Struttura in legno",
  "Casa in legno",
  "Edilizia alberghiera / turistica",
  "Facciata in legno",
  "Risanamento / Restauro",
  "Capannone / Edificio industriale",
  "Terrazza / Balcone",
  "Altro"
];

export default function Kontakt() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { lang } = useLanguage();

  const projektarten = lang === 'it' ? projektartenIT : projektartenDE;

  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.06, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  });

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.nachricht) {
      setError(lang === 'it'
        ? 'Compilate nome, e-mail e messaggio.'
        : 'Bitte füllen Sie Name, E-Mail und Nachricht aus.'
      );
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/send.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, type: 'kontakt' }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(true);
        setForm(initialForm);
      } else {
        setError(data.error || (lang === 'it' ? 'Errore durante l\'invio.' : 'Fehler beim Senden.'));
      }
    } catch (_) {
      setError(lang === 'it'
        ? 'Errore di connessione. Scrivete direttamente a info@holzbuddy.team'
        : 'Verbindungsfehler. Bitte schreiben Sie direkt an info@holzbuddy.team'
      );
    }
    setLoading(false);
  };

  return (
    <div className="pt-20 md:pt-24">
      {/* Header */}
      <section className="section-gap bg-hb-off-white border-b border-hb-border">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-6 reveal">
            <T de="Kontakt" it="Contatto" />
          </span>
          <h1 className="section-title max-w-2xl mb-6 reveal reveal-delay-1" data-testid="kontakt-heading">
            <T de="Lassen Sie uns Ihr Projekt besprechen." it="Parliamo del vostro progetto." />
          </h1>
          <p className="section-subtitle max-w-xl reveal reveal-delay-2">
            <T de="Kein Formular-Dschungel. Nur die wichtigsten Informationen – und wir melden uns persönlich bei Ihnen."
               it="Nessun labirinto di moduli. Solo le informazioni essenziali – e vi contatteremo personalmente." />
          </p>
          <div className="mt-6 reveal reveal-delay-3">
            <a
              href="/anfrage"
              data-testid="konfigurator-link"
              className="inline-flex items-center gap-2 text-sm text-hb-wood font-semibold hover:text-hb-wood-dark transition-colors font-inter"
            >
              <T de="Lieber den Anfrage-Konfigurator nutzen?" it="Preferite usare il configuratore di richiesta?" /> →
            </a>
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="section-gap bg-white" data-testid="kontakt-content">
        <div className="container-hb">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <span className="label-text text-hb-wood block mb-8">
                <T de="Anfrage" it="Richiesta" />
              </span>

              {success ? (
                <div data-testid="success-message" className="bg-hb-off-white border border-hb-border p-10 text-center">
                  <div className="w-12 h-12 bg-hb-wood/10 flex items-center justify-center mx-auto mb-5">
                    <Check size={24} className="text-hb-wood" />
                  </div>
                  <h3 className="text-xl font-manrope font-bold text-hb-dark mb-3">
                    <T de="Anfrage gesendet." it="Richiesta inviata." />
                  </h3>
                  <p className="text-sm text-hb-text font-inter">
                    <T de="Vielen Dank. Das Holzbuddy Team meldet sich in der Regel innerhalb von 24 Stunden bei Ihnen."
                       it="Grazie mille. Il team Holzbuddy vi contatterà di norma entro 24 ore." />
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="kontakt-form" className="space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                        <T de="Name" it="Nome" /> <span className="text-hb-wood">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder={lang === 'it' ? 'Il vostro nome' : 'Ihr Name'}
                        data-testid="input-name"
                        className="input-hb"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                        E-Mail <span className="text-hb-wood">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder={lang === 'it' ? 'vostro@email.com' : 'ihre@email.com'}
                        data-testid="input-email"
                        className="input-hb"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                        <T de="Telefon" it="Telefono" />
                      </label>
                      <input
                        type="tel"
                        name="telefon"
                        value={form.telefon}
                        onChange={handleChange}
                        placeholder="+39 ..."
                        data-testid="input-telefon"
                        className="input-hb"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                        <T de="Ort / Gemeinde" it="Località / Comune" />
                      </label>
                      <input
                        type="text"
                        name="ort"
                        value={form.ort}
                        onChange={handleChange}
                        placeholder={lang === 'it' ? 'Es. Merano, Bolzano...' : 'z.B. Meran, Bozen...'}
                        data-testid="input-ort"
                        className="input-hb"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                      <T de="Projektart" it="Tipo di progetto" />
                    </label>
                    <select
                      name="projektart"
                      value={form.projektart}
                      onChange={handleChange}
                      data-testid="input-projektart"
                      className="input-hb bg-transparent"
                    >
                      <option value="">{lang === 'it' ? 'Selezionate...' : 'Bitte wählen...'}</option>
                      {projektarten.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs tracking-widest uppercase text-hb-muted font-semibold mb-2">
                      <T de="Nachricht" it="Messaggio" /> <span className="text-hb-wood">*</span>
                    </label>
                    <textarea
                      name="nachricht"
                      value={form.nachricht}
                      onChange={handleChange}
                      placeholder={lang === 'it' ? 'Descrivete brevemente il vostro progetto...' : 'Beschreiben Sie Ihr Vorhaben kurz...'}
                      data-testid="input-nachricht"
                      rows={6}
                      className="input-hb resize-none"
                      required
                    />
                  </div>

                  {error && (
                    <p data-testid="form-error" className="text-sm text-red-500 font-inter">{error}</p>
                  )}

                  <div className="flex items-center gap-4">
                    <button
                      type="submit"
                      data-testid="submit-btn"
                      disabled={loading}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading
                        ? <T de="Wird gesendet..." it="Invio in corso..." />
                        : <T de="Anfrage senden" it="Invia richiesta" />
                      }
                    </button>
                    <p className="text-xs text-hb-muted font-inter">
                      <T de="* Pflichtfelder" it="* Campi obbligatori" />
                    </p>
                  </div>

                  <p className="text-xs text-hb-muted font-inter">
                    <T de="Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten gemäß unserer Datenschutzerklärung zu."
                       it="Inviando il modulo, accettate il trattamento dei vostri dati secondo la nostra informativa sulla privacy." />
                  </p>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <span className="label-text text-hb-wood block mb-6">
                  <T de="Kontaktdaten" it="Dati di contatto" />
                </span>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <MapPin size={16} className="text-hb-wood mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-hb-text font-inter">
                      <strong className="text-hb-dark block mb-1">Holzbuddy · Hafner EC GmbH</strong>
                      Sinich-Bach-Str. 60<br />
                      I-39012 Meran (BZ)<br />
                      <T de="Südtirol, Italien" it="Alto Adige, Italia" />
                    </div>
                  </li>
                  <li className="flex items-center gap-4">
                    <Phone size={16} className="text-hb-wood flex-shrink-0" />
                    <a href="tel:+390473238030" data-testid="kontakt-phone" className="text-sm text-hb-text hover:text-hb-dark transition-colors font-inter">
                      +39 0473 238 030
                    </a>
                  </li>
                  <li className="flex items-center gap-4">
                    <Mail size={16} className="text-hb-wood flex-shrink-0" />
                    <a href="mailto:info@holzbuddy.team" data-testid="kontakt-email" className="text-sm text-hb-text hover:text-hb-dark transition-colors font-inter">
                      info@holzbuddy.team
                    </a>
                  </li>
                  <li className="flex items-center gap-4">
                    <Clock size={16} className="text-hb-wood flex-shrink-0" />
                    <div className="text-sm text-hb-text font-inter">
                      <T de="Montag – Freitag" it="Lunedì – Venerdì" /><br />
                      7:00 – 18:00 <T de="Uhr" it="" />
                    </div>
                  </li>
                </ul>
              </div>

              <div className="border-t border-hb-border pt-8">
                <h3 className="text-sm font-manrope font-bold text-hb-dark mb-3">
                  <T de="Rückruf bevorzugt?" it="Preferite essere richiamati?" />
                </h3>
                <p className="text-sm text-hb-text font-inter mb-4">
                  <T de="Hinterlassen Sie einfach Ihre Nummer. Wir rufen zurück."
                     it="Lasciate il vostro numero. Vi richiameremo." />
                </p>
                <a href="tel:+390473238030" data-testid="rueckruf-btn" className="btn-secondary text-xs">
                  <T de="Rückruf anfordern" it="Richiedere una chiamata" />
                </a>
              </div>

              <div className="bg-hb-off-white p-6">
                <p className="text-xs text-hb-muted font-inter leading-relaxed">
                  <T de="Holzbuddy ist eine Marke der" it="Holzbuddy è un marchio di" /><br />
                  <strong className="text-hb-dark">Hafner EC GmbH</strong><br />
                  MwSt.-Nr. / P.IVA: 02706840218<br />
                  Empfängercode / Cod. Destinatario: T04ZHR3
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="bg-hb-off-white border-t border-hb-border" data-testid="maps-section">
        <div className="container-hb py-12">
          <span className="label-text text-hb-wood block mb-5">
            <T de="Standort" it="Posizione" />
          </span>
          <h2 className="text-2xl font-manrope font-bold text-hb-dark mb-6">
            Holzbuddy · Hafner EC GmbH <T de="in Meran" it="a Merano" />
          </h2>
          <div className="aspect-[16/7] overflow-hidden border border-hb-border">
            <iframe
              title="Holzbuddy – Hafner EC GmbH, Meran"
              src="https://maps.google.com/maps?q=Sinich-Bach-Str+60+Meran+Italy&t=&z=15&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-maps"
            />
          </div>
          <p className="text-xs text-hb-muted font-inter mt-3">
            Sinich-Bach-Str. 60 · I-39012 Meran (BZ) · <T de="Südtirol, Italien" it="Alto Adige, Italia" />
          </p>
        </div>
      </section>
    </div>
  );
}
